/**
 * Photoshop-style curves: smooth tonal remapping driven by draggable control
 * points. Each channel (composite RGB + per-channel R/G/B) is described by a set
 * of control points in 0–255 input/output space. We fit a natural cubic spline
 * through the points to produce a 256-entry lookup table (LUT), exactly like the
 * smooth curve in Photoshop's Curves dialog. The composite LUT is applied first,
 * then the per-channel LUTs, so adjustments compose the same way Photoshop does.
 */

export interface CurvePoint {
  /** Input value 0–255 (horizontal axis). */
  x: number;
  /** Output value 0–255 (vertical axis). */
  y: number;
}

export type CurveChannel = 'rgb' | 'r' | 'g' | 'b';

export interface CurvesState {
  rgb: CurvePoint[];
  r: CurvePoint[];
  g: CurvePoint[];
  b: CurvePoint[];
}

export interface CurvesLuts {
  rgb: Uint8ClampedArray;
  r: Uint8ClampedArray;
  g: Uint8ClampedArray;
  b: Uint8ClampedArray;
}

/** A straight identity curve (no change): bottom-left to top-right. */
export const IDENTITY_POINTS: CurvePoint[] = [
  { x: 0, y: 0 },
  { x: 255, y: 255 },
];

export const createIdentityCurves = (): CurvesState => ({
  rgb: IDENTITY_POINTS.map((p) => ({ ...p })),
  r: IDENTITY_POINTS.map((p) => ({ ...p })),
  g: IDENTITY_POINTS.map((p) => ({ ...p })),
  b: IDENTITY_POINTS.map((p) => ({ ...p })),
});

const clamp = (v: number, min: number, max: number) =>
  v < min ? min : v > max ? max : v;

/** True when a channel's points form the unchanged identity curve. */
const isIdentity = (points: CurvePoint[]): boolean =>
  points.length === 2 &&
  points[0].x === 0 &&
  points[0].y === 0 &&
  points[1].x === 255 &&
  points[1].y === 255;

export const isIdentityCurves = (curves: CurvesState): boolean =>
  isIdentity(curves.rgb) &&
  isIdentity(curves.r) &&
  isIdentity(curves.g) &&
  isIdentity(curves.b);

/**
 * Build a 256-entry LUT from control points using a natural cubic spline.
 * Points are sorted and de-duplicated by x. With two points the result is a
 * straight line; with more points it is a smooth curve through every point.
 */
export function buildLut(pointsInput: CurvePoint[]): Uint8ClampedArray {
  const lut = new Uint8ClampedArray(256);

  // Sort by x and drop duplicate x values (keep the last one set).
  const sorted = [...pointsInput].sort((a, b) => a.x - b.x);
  const points: CurvePoint[] = [];
  for (const p of sorted) {
    const x = clamp(Math.round(p.x), 0, 255);
    const y = clamp(p.y, 0, 255);
    if (points.length && points[points.length - 1].x === x) {
      points[points.length - 1].y = y;
    } else {
      points.push({ x, y });
    }
  }

  if (points.length === 0) {
    for (let i = 0; i < 256; i++) lut[i] = i;
    return lut;
  }
  if (points.length === 1) {
    const y = clamp(Math.round(points[0].y), 0, 255);
    for (let i = 0; i < 256; i++) lut[i] = y;
    return lut;
  }

  const n = points.length;
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);

  // Natural cubic spline: solve for second derivatives (y2) via the standard
  // tridiagonal algorithm (Numerical Recipes "spline").
  const y2 = new Array(n).fill(0);
  const u = new Array(n).fill(0);
  for (let i = 1; i < n - 1; i++) {
    const sig = (xs[i] - xs[i - 1]) / (xs[i + 1] - xs[i - 1]);
    const p = sig * y2[i - 1] + 2;
    y2[i] = (sig - 1) / p;
    let du =
      (ys[i + 1] - ys[i]) / (xs[i + 1] - xs[i]) -
      (ys[i] - ys[i - 1]) / (xs[i] - xs[i - 1]);
    du = (6 * du) / (xs[i + 1] - xs[i - 1]) - sig * u[i - 1];
    u[i] = du;
  }
  for (let i = n - 2; i >= 0; i--) {
    y2[i] = y2[i] * y2[i + 1] + u[i];
  }

  const splineAt = (x: number): number => {
    // Find the interval [xs[klo], xs[khi]] containing x.
    let klo = 0;
    let khi = n - 1;
    while (khi - klo > 1) {
      const k = (khi + klo) >> 1;
      if (xs[k] > x) khi = k;
      else klo = k;
    }
    const h = xs[khi] - xs[klo];
    if (h === 0) return ys[klo];
    const a = (xs[khi] - x) / h;
    const b = (x - xs[klo]) / h;
    return (
      a * ys[klo] +
      b * ys[khi] +
      (((a * a * a - a) * y2[klo] + (b * b * b - b) * y2[khi]) * (h * h)) / 6
    );
  };

  const firstX = xs[0];
  const lastX = xs[n - 1];
  const firstY = ys[0];
  const lastY = ys[n - 1];

  for (let i = 0; i < 256; i++) {
    let v: number;
    if (i <= firstX) v = firstY; // flat clamp before first point
    else if (i >= lastX) v = lastY; // flat clamp after last point
    else v = splineAt(i);
    lut[i] = clamp(Math.round(v), 0, 255);
  }
  return lut;
}

export function buildCurvesLuts(curves: CurvesState): CurvesLuts {
  return {
    rgb: buildLut(curves.rgb),
    r: buildLut(curves.r),
    g: buildLut(curves.g),
    b: buildLut(curves.b),
  };
}

/** Sample a curve's spline as a continuous function for drawing the line. */
export function sampleCurve(points: CurvePoint[], steps = 256): CurvePoint[] {
  const lut = buildLut(points);
  const out: CurvePoint[] = [];
  for (let i = 0; i < steps; i++) {
    const x = (i / (steps - 1)) * 255;
    out.push({ x, y: lut[clamp(Math.round(x), 0, 255)] });
  }
  return out;
}

/**
 * Apply curves LUTs to ImageData in place. Composite (rgb) LUT is applied first,
 * then each per-channel LUT — matching Photoshop's compose order.
 */
export function applyCurvesToImageData(
  imageData: ImageData,
  luts: CurvesLuts
): void {
  const { rgb, r, g, b } = luts;
  const d = imageData.data;
  const len = d.length;
  for (let i = 0; i < len; i += 4) {
    d[i] = r[rgb[d[i]]];
    d[i + 1] = g[rgb[d[i + 1]]];
    d[i + 2] = b[rgb[d[i + 2]]];
    // Alpha (d[i + 3]) untouched.
  }
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
}

/**
 * Compute a luminance histogram (256 bins) from an image, downsampled for speed.
 * Used to paint the histogram behind the curve grid.
 */
export async function computeHistogram(
  url: string,
  sampleSize = 256
): Promise<number[]> {
  const img = await loadImage(url);
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  const scale = Math.min(1, sampleSize / Math.max(w, h));
  const sw = Math.max(1, Math.round(w * scale));
  const sh = Math.max(1, Math.round(h * scale));
  const canvas = document.createElement('canvas');
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext('2d');
  const hist = new Array(256).fill(0);
  if (!ctx) return hist;
  ctx.drawImage(img, 0, 0, sw, sh);
  const data = ctx.getImageData(0, 0, sw, sh).data;
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 8) continue;
    const y = Math.round(
      0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]
    );
    hist[clamp(y, 0, 255)]++;
  }
  return hist;
}

/**
 * Apply curves to a full-resolution image URL and return a JPEG/PNG Blob.
 * Preserves the source dimensions exactly.
 */
export async function applyCurvesToImageUrlAsBlob(
  url: string,
  curves: CurvesState,
  mimeType: 'image/jpeg' | 'image/png' = 'image/jpeg',
  quality = 0.95
): Promise<Blob> {
  const img = await loadImage(url);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas context failed');
  ctx.drawImage(img, 0, 0);
  if (!isIdentityCurves(curves)) {
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    applyCurvesToImageData(imageData, buildCurvesLuts(curves));
    ctx.putImageData(imageData, 0, 0);
  }
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))),
      mimeType,
      quality
    );
  });
}
